import { Region } from '@chiroup/core/types/Condition.type';
import { ObjectiveNote } from '@chiroup/core/types/PatientVisit.type';
import { JOINT_REGION_TO_REGION, ROM_REGION_OPTIONS } from '../../../../constants';

/**
 * Seed the unified objective from a newly selected diagnosis — a pure port of
 * what the real Assessment step does on condition add (AssessmentStepV2's
 * NotesConditionsPickerModal onChange): suggested orthopedic tests get
 * typicallyPositive/Negative flags WITHOUT values (so they render as
 * suggestions the provider must accept), myofascial muscles are added
 * touched, and the condition's regions open Joints + Range of Motion.
 *
 * Kept behaviorally identical so the sandbox's "your diagnosis just did the
 * work" moment is the same one the full EHR produces. Deselecting a diagnosis
 * intentionally leaves the objective untouched — that also mirrors the EHR.
 */
export const seedUnifiedObjectiveFromCondition = (
  current: ObjectiveNote | undefined,
  data: {
    regions?: string[];
    muscles?: number[];
    typicallyPositiveEvals?: number[];
    typicallyNegativeEvals?: number[];
  },
  complaintId: string
): ObjectiveNote => {
  const next: ObjectiveNote = { ...(current || {}) };

  // Unlike the Yjs-backed original (which mutates a getField copy), sandbox
  // state lives in useState — entries must be copied, not mutated, or React
  // StrictMode's double-invoked updater appends complaintIds twice.
  const withComplaintId = <T extends { complaintIds?: string[] | Set<string> }>(entry: T): T => {
    const ids = Array.isArray(entry.complaintIds)
      ? [...entry.complaintIds]
      : Array.from(entry.complaintIds || []);

    if (!ids.includes(complaintId)) ids.push(complaintId);

    return { ...entry, complaintIds: ids };
  };

  if (data.typicallyPositiveEvals?.length || data.typicallyNegativeEvals?.length) {
    next.Orthopedic = { ...next.Orthopedic, detail: { ...(next.Orthopedic?.detail || {}) } };

    data.typicallyPositiveEvals?.forEach(evalId => {
      if (!next.Orthopedic!.detail[evalId]) {
        next.Orthopedic!.detail[evalId] = {
          ID: evalId,
          typicallyPositive: true,
          complaintIds: [complaintId],
          addedManually: false,
          touched: false,
        };
      } else {
        next.Orthopedic!.detail[evalId] = withComplaintId(next.Orthopedic!.detail[evalId]);
      }
    });

    data.typicallyNegativeEvals?.forEach(evalId => {
      if (!next.Orthopedic!.detail[evalId]) {
        next.Orthopedic!.detail[evalId] = {
          ID: evalId,
          typicallyNegative: true,
          complaintIds: [complaintId],
          addedManually: false,
          touched: false,
        };
      } else {
        next.Orthopedic!.detail[evalId] = withComplaintId(next.Orthopedic!.detail[evalId]);
      }
    });
  }

  if (data.muscles?.length) {
    next.Myofascial = { ...next.Myofascial, detail: { ...(next.Myofascial?.detail || {}) } };

    data.muscles.forEach(muscleId => {
      if (!next.Myofascial!.detail[muscleId]) {
        next.Myofascial!.detail[muscleId] = {
          complaintIds: [complaintId],
          addedManually: false,
          touched: true,
        };
      } else {
        next.Myofascial!.detail[muscleId] = withComplaintId(next.Myofascial!.detail[muscleId]);
      }
    });
  }

  if (data.regions?.length) {
    next.Joints = { ...next.Joints, detail: { ...(next.Joints?.detail || {}) } };
    next.RangeOfMotion = {
      ...next.RangeOfMotion,
      detail: { ...(next.RangeOfMotion?.detail || {}) },
    };

    data.regions.forEach(region => {
      const jointRegion = JOINT_REGION_TO_REGION[region as Region] || region;

      if (!next.Joints!.detail[jointRegion]) {
        next.Joints!.detail[jointRegion] = { complaintIds: [complaintId], addedManually: false };
      } else {
        next.Joints!.detail[jointRegion] = withComplaintId(next.Joints!.detail[jointRegion]);
      }

      if (ROM_REGION_OPTIONS[region as Region]) {
        if (!next.RangeOfMotion!.detail[region]) {
          next.RangeOfMotion!.detail[region] = {
            complaintIds: [complaintId],
            addedManually: false,
          };
        } else {
          next.RangeOfMotion!.detail[region] = withComplaintId(next.RangeOfMotion!.detail[region]);
        }
      }
    });
  }

  return next;
};
